import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Award,
  TrendingUp,
  Trophy,
  User,
  Globe,
  Crown,
} from 'lucide-react';
import { usePaymentGate } from '@/payment';
import { tryPracticeNavigation } from '@/lib/practiceNavigationGuard';
import { CONTENT_LOCALES, getContentLocale, setContentLocale, type ContentLocale } from '@/lib/contentLocale';
import { useEffect, useState } from 'react';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [locale, setLocale] = useState<ContentLocale>(getContentLocale());

  // Mobile browsers (iOS Chrome's bottom toolbar, Safari's floating URL bar)
  // don't reliably shrink 100dvh, which pushes the exam footer/nav out of view.
  // visualViewport.height reports the *actually visible* area, so we mirror it
  // into --app-height and size the shell to it. Falls back to 100dvh/100vh.
  useEffect(() => {
    const vv = window.visualViewport;
    const setAppHeight = () => {
      const h = vv ? vv.height : window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${Math.round(h)}px`);
    };
    setAppHeight();
    vv?.addEventListener('resize', setAppHeight);
    window.addEventListener('resize', setAppHeight);
    window.addEventListener('orientationchange', setAppHeight);
    return () => {
      vv?.removeEventListener('resize', setAppHeight);
      window.removeEventListener('resize', setAppHeight);
      window.removeEventListener('orientationchange', setAppHeight);
    };
  }, []);

  const handleLocaleChange = (nextLocale: ContentLocale) => {
    setContentLocale(nextLocale);
    setLocale(nextLocale);
    window.location.reload();
  };

  // Entrée "Premium" : n'existe que si le module de paiement est activé
  // (admin). Abonné (ou expiré) → gestion de l'abonnement ; sinon → plans.
  const { paymentsEnabled, canPurchaseInApp, hasActiveSubscription, isExpired } = usePaymentGate();

  const menuItems = [
    { icon: Home, label: 'Accueil', path: '/dashboard' },
    { icon: Award, label: 'Certifs', path: '/certifications' },
    { icon: TrendingUp, label: 'Stats', path: '/stats' },
    { icon: Trophy, label: 'Rang', path: '/leaderboard' },
    ...(paymentsEnabled && (canPurchaseInApp || hasActiveSubscription || isExpired)
      ? [{
          icon: Crown,
          label: 'Premium',
          path: !canPurchaseInApp || hasActiveSubscription || isExpired ? '/subscription' : '/plans',
        }]
      : []),
    { icon: User, label: 'Profil', path: '/profile' },
  ];

  const isActive = (path: string) => location.pathname === path;

  // Examen en cours = expérience immersive plein écran : on masque la barre
  // d'onglets et le sélecteur de langue pour éviter la collision avec le footer
  // de navigation et un rechargement accidentel (qui redémarrerait l'examen).
  const isImmersive = location.pathname.endsWith('/simulation');

  const handleNavigate = (path: string) => {
    if (tryPracticeNavigation(path)) {
      navigate(path);
    }
  };

  return (
    // height:100dvh (inline) sizes to the *visible* viewport, excluding the
    // mobile browser's dynamic toolbar/URL bar so the bottom footer/nav is never
    // hidden behind it. h-screen (100vh) stays as the fallback for old browsers.
    <div className="flex h-screen bg-app overflow-hidden" style={{ height: 'var(--app-height, 100dvh)' }}>
      <div className="hidden lg:flex lg:flex-col lg:w-[72px] xl:w-[220px] bg-app border-r border-line">
        <div className="flex items-center gap-3 px-4 py-5 border-b border-line">
          <img src="/logo.svg" alt="Exam Copilot" className="w-10 h-10 rounded-xl flex-shrink-0" />
          <div className="hidden xl:block">
            <span className="text-sm font-bold text-fg">Exam Copilot</span>
          </div>
        </div>

        <div className="px-3 pb-3 hidden xl:block">
          <div className="flex gap-1 rounded-lg bg-overlay-1 p-1">
            {CONTENT_LOCALES.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleLocaleChange(option.value)}
                className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                  locale === option.value
                    ? 'bg-green-500/20 text-green-300'
                    : 'text-fg-subtle hover:text-fg-muted'
                }`}
              >
                {option.value.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="px-3 pb-3 xl:hidden flex justify-center">
          <button
            onClick={() => handleLocaleChange(locale === 'fr' ? 'en' : 'fr')}
            className="w-10 h-10 rounded-xl bg-overlay-1 flex items-center justify-center text-xs font-bold text-fg-subtle hover:text-fg-muted transition-colors"
            title={locale === 'fr' ? 'Switch to English' : 'Passer en Français'}
          >
            {locale.toUpperCase()}
          </button>
        </div>

        <nav className="flex-1 flex flex-col gap-1 px-3 py-4">
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavigate(item.path)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                isActive(item.path)
                  ? 'bg-green-500/15 text-green-400'
                  : 'text-fg-subtle hover:text-fg-muted hover:bg-overlay-1'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-medium hidden xl:block">{item.label}</span>
              {isActive(item.path) && (
                <div className="ml-auto w-1.5 h-1.5 bg-green-400 rounded-full hidden xl:block" />
              )}
            </button>
          ))}
        </nav>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 overflow-hidden">{children}</main>
      </div>

      {!isImmersive && (
        <button
          onClick={() => handleLocaleChange(locale === 'fr' ? 'en' : 'fr')}
          className="lg:hidden fixed top-3 right-3 z-50 flex items-center gap-1.5 px-2.5 py-1.5 bg-surface/90 backdrop-blur-sm border border-line rounded-full text-xs font-medium text-fg-muted hover:text-fg-muted transition-colors"
        >
          <Globe className="w-3.5 h-3.5" />
          {locale.toUpperCase()}
        </button>
      )}

      <nav className={`${isImmersive ? 'hidden' : 'lg:hidden'} fixed bottom-0 left-0 right-0 bg-app/95 backdrop-blur-xl border-t border-line px-2 py-2 z-50`}>
        <div className="flex items-center justify-around">
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavigate(item.path)}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all duration-200 ${
                isActive(item.path)
                  ? 'text-green-400'
                  : 'text-fg-subtle'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
